import { MoreVertical, Copy, Trash2, ExternalLink } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const PLATFORM_COLORS = {
    meta: { bg: 'rgba(24, 119, 242, 0.1)', color: '#1877F2', label: 'Meta' },
    google: { bg: 'rgba(234, 67, 53, 0.1)', color: '#EA4335', label: 'Google' },
    tiktok: { bg: 'rgba(0, 0, 0, 0.1)', color: '#000000', label: 'TikTok' },
    linkedin: { bg: 'rgba(10, 102, 194, 0.1)', color: '#0A66C2', label: 'LinkedIn' }
};

const STATUS_STYLES = {
    draft: { bg: 'var(--warning-100)', color: 'var(--warning-700)' },
    pending: { bg: 'var(--primary-100)', color: 'var(--primary-700)' }
};

export default function FunnelRow({ funnel, onLoad, onDuplicate, onDelete }) {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const platform = PLATFORM_COLORS[funnel.platform] || PLATFORM_COLORS.meta;
    const status = STATUS_STYLES[funnel.status] || STATUS_STYLES.draft;

    // Count node types
    const nodes = funnel.nodes || [];
    const campaignCount = nodes.filter(n => n.type === 'campaign').length;
    const adSetCount = nodes.filter(n => n.type === 'adset').length;
    const adCount = nodes.filter(n => n.type === 'ad').length;

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown date';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffInMs = now - date;
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInDays === 0) {
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            if (diffInHours === 0) {
                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
            }
            return `${diffInHours}h ago`;
        } else if (diffInDays === 1) {
            return 'Yesterday';
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return date.toLocaleDateString();
        }
    };

    const handleRowClick = () => {
        onLoad(funnel.id);
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleDuplicate = (e) => {
        e.stopPropagation();
        setShowMenu(false);
        onDuplicate(funnel.id);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        setShowMenu(false);
        onDelete(funnel.id);
    };

    return (
        <div
            onClick={handleRowClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                backgroundColor: 'var(--color-surface)'
            }}
            className="funnel-row"
        >
            {/* Platform badge */}
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 10px',
                backgroundColor: platform.bg,
                color: platform.color,
                borderRadius: 'var(--radius-full)',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                minWidth: '70px'
            }}>
                {platform.label}
            </span>

            {/* Funnel name */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {funnel.name || 'Untitled Funnel'}
                </h4>
            </div>

            {/* Status badge */}
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                backgroundColor: status.bg,
                color: status.color,
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'uppercase'
            }}>
                {funnel.status || 'draft'}
            </span>

            {/* Node counts */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                fontSize: '0.75rem',
                color: 'var(--color-text-secondary)',
                minWidth: '180px'
            }}>
                <span>{campaignCount} campaigns</span>
                <span>·</span>
                <span>{adSetCount} ad sets</span>
                <span>·</span>
                <span>{adCount} ads</span>
            </div>

            {/* Last updated */}
            <div style={{
                fontSize: '0.75rem',
                color: 'var(--color-text-tertiary)',
                minWidth: '100px',
                textAlign: 'right'
            }}>
                {formatDate(funnel.updatedAt)}
            </div>

            {/* Menu button */}
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                    onClick={handleMenuClick}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 'var(--space-1)',
                        color: 'var(--color-text-tertiary)',
                        borderRadius: 'var(--radius-sm)'
                    }}
                >
                    <MoreVertical size={16} />
                </button>
                {showMenu && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 'var(--space-1)',
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 100,
                        minWidth: '140px',
                        overflow: 'hidden'
                    }}>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleRowClick(); setShowMenu(false); }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                width: '100%',
                                padding: 'var(--space-2) var(--space-3)',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                textAlign: 'left'
                            }}
                        >
                            <ExternalLink size={14} />
                            Open
                        </button>
                        <button
                            onClick={handleDuplicate}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                width: '100%',
                                padding: 'var(--space-2) var(--space-3)',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                textAlign: 'left'
                            }}
                        >
                            <Copy size={14} />
                            Duplicate
                        </button>
                        <button
                            onClick={handleDelete}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                width: '100%',
                                padding: 'var(--space-2) var(--space-3)',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: 'var(--color-error)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                textAlign: 'left'
                            }}
                        >
                            <Trash2 size={14} />
                            Delete
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                .funnel-row:hover {
                    background-color: var(--color-surface-hover) !important;
                }
            `}</style>
        </div>
    );
}
